import CtaArrow from "@/components/svg/CtaArrow";
import Tag from "@/components/Tag";
import TagIcon from "@/components/TagIcon";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

const richTextBase = {
  strong: ({ children }) => <strong className="md">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
};

/** `text` used to be a key text field: keep old documents rendering until they are re-saved. */
const toRichText = (field) =>
  typeof field === "string" && field
    ? [{ type: "paragraph", text: field, spans: [], direction: "ltr" }]
    : field;

/**
 * Group items whose project is picked and fetched with its preview data.
 * @param {import("@prismicio/client").Content.ProjectListSliceDefaultPrimaryProjectsItem[]} projects
 */
const getItems = (projects) =>
  Array.isArray(projects)
    ? projects.filter(
        ({ project }) =>
          isFilled.contentRelationship(project) &&
          (isFilled.image(project.data?.preview_image) ||
            isFilled.richText(project.data?.preview_title)),
      )
    : [];

const cardTitleComponents = (className) => ({
  heading1: ({ children }) => <p className={className}>{children}</p>,
  heading2: ({ children }) => <p className={className}>{children}</p>,
  heading3: ({ children }) => <p className={className}>{children}</p>,
  paragraph: ({ children }) => <p className={className}>{children}</p>,
});

/**
 * Default card: full photo, info panel on the bottom. On hover (desktop) the
 * panel grows to reveal the project tags.
 * @param {{ item: import("@prismicio/client").Content.ProjectListSliceDefaultPrimaryProjectsItem }} props
 */
const DefaultCard = ({ item: { project } }) => {
  const { preview_image, preview_title, tags } = project.data;
  const filledTags = Array.isArray(tags)
    ? tags.filter((item) => isFilled.keyText(item.tag))
    : [];

  return (
    <PrismicNextLink
      prefetch={false}
      field={project}
      className="group relative flex h-[400px] w-full flex-col justify-end overflow-hidden rounded-[30px] p-[30px] lg:h-[466px] lg:rounded-[20px]"
    >
      {isFilled.image(preview_image) && (
        <PrismicNextImage
          field={preview_image}
          fallbackAlt=""
          fill
          className="object-cover"
          sizes="(max-width: 1280px) 50vw, 33vw"
        />
      )}

      <div className="relative z-10 w-full rounded-[20px] bg-bleu p-5">
        <div className="flex items-center justify-between gap-2.5">
          {isFilled.richText(preview_title) && (
            <PrismicRichText
              field={preview_title}
              components={cardTitleComponents("p-xl-n sbd leading-[30px] text-beige-30")}
            />
          )}
          <CtaArrow className="h-3.5 w-2.5 text-beige-30 transition-opacity duration-300 group-hover:opacity-0 group-focus-visible:opacity-0" />
        </div>

        {filledTags.length > 0 && (
          <div className="hidden grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-300 group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-visible:grid-rows-[1fr] group-focus-visible:opacity-100 lg:grid">
            <div className="overflow-hidden">
              <div className="flex flex-wrap gap-2.5 pt-4">
                {filledTags.map((item, index) => (
                  <Tag key={`tag-${index}`} color="bleu-50" text={item.tag} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </PrismicNextLink>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.ProjectListSliceDefault>} props
 */
const ProjectListDefault = ({ slice }) => {
  const { title, text, projects } = slice.primary;
  const richText = toRichText(text);
  const filledProjects = getItems(projects);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x pb-15 pt-25 lg:pb-25 lg:pt-[130px]"
    >
      <div className="framing-lg flex flex-col gap-10 lg:gap-15">
        {(isFilled.richText(title) || isFilled.richText(richText)) && (
          <div className="flex flex-col gap-5 lg:max-w-[593px] lg:gap-[30px]">
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="h1-n lg:dk text-bleu">{children}</h1>
                  ),
                  heading2: ({ children }) => (
                    <h1 className="h1-n lg:dk text-bleu">{children}</h1>
                  ),
                }}
              />
            )}
            {isFilled.richText(richText) && (
              <div className="lg:max-w-[510px]">
                <PrismicRichText
                  field={richText}
                  components={{
                    ...richTextBase,
                    paragraph: ({ children }) => (
                      <p className="p-lg-n lg:p-xl-n lg:lt text-bleu">{children}</p>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        )}

        {filledProjects.length > 0 && (
          <ul className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-3">
            {filledProjects.map((item, index) => (
              <li key={`project-${index}`}>
                <DefaultCard item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/**
 * Light card: photo on top, project type and title below.
 * @param {{ item: import("@prismicio/client").Content.ProjectListSliceLightPrimaryProjectsItem }} props
 */
const LightCard = ({ item: { project, author_picture, author_name } }) => {
  const { preview_image, preview_title, type } = project.data;
  // The author tag only shows when both its picture and its name are filled.
  const hasAuthor = isFilled.image(author_picture) && isFilled.keyText(author_name);

  return (
    <PrismicNextLink
      prefetch={false}
      field={project}
      className="flex flex-col gap-5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bleu"
    >
      {isFilled.image(preview_image) && (
        <div className="relative h-[280px] w-full overflow-hidden rounded-[10px]">
          <PrismicNextImage
            field={preview_image}
            fallbackAlt=""
            fill
            className="object-cover"
            sizes="(max-width: 1280px) 50vw, 33vw"
          />
          {hasAuthor && (
            <div className="absolute left-5 top-5 flex items-center gap-2.5 rounded-[50px] bg-beige py-[5px] pl-2 pr-3">
              <span className="relative size-7 shrink-0 overflow-hidden rounded-full">
                <PrismicNextImage
                  field={author_picture}
                  fallbackAlt=""
                  fill
                  className="object-cover"
                  sizes="28px"
                />
              </span>
              <span className="cta-text bd text-bleu">{author_name}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col gap-3">
        {isFilled.select(type) && <TagIcon tag={type} />}
        {isFilled.richText(preview_title) && (
          <PrismicRichText
            field={preview_title}
            components={cardTitleComponents("p-lg-n sbd text-bleu")}
          />
        )}
      </div>
    </PrismicNextLink>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.ProjectListSliceLight>} props
 */
const ProjectListLight = ({ slice }) => {
  const { sub_header, title, text, projects } = slice.primary;
  const filledProjects = getItems(projects);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-blanc padding-x padding-y-md"
    >
      <div className="framing-lg flex flex-col gap-[60px]">
        {(isFilled.keyText(sub_header) ||
          isFilled.richText(title) ||
          isFilled.richText(text)) && (
          <div className="flex flex-col gap-2.5 lg:max-w-[575px]">
            <div className="flex flex-col gap-1.5">
              {isFilled.keyText(sub_header) && (
                <p className="section-text-n text-rouge">{sub_header}</p>
              )}
              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="h2-n lg:dk text-bleu">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="h2-n lg:dk text-bleu">{children}</h3>
                    ),
                  }}
                />
              )}
            </div>
            {isFilled.richText(text) && (
              <PrismicRichText
                field={text}
                components={{
                  ...richTextBase,
                  paragraph: ({ children }) => (
                    <p className="p-lg-n text-bleu">{children}</p>
                  ),
                }}
              />
            )}
          </div>
        )}

        {filledProjects.length > 0 && (
          <ul className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-3 xl:gap-x-7">
            {filledProjects.map((item, index) => (
              <li key={`project-${index}`}>
                <LightCard item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectListSlice} ProjectListSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectListSlice>} ProjectListProps
 * @type {import("react").FC<ProjectListProps>}
 */
const ProjectList = ({ slice }) => {
  switch (slice.variation) {
    case "light":
      return <ProjectListLight slice={slice} />;
    default:
      return <ProjectListDefault slice={slice} />;
  }
};

export default ProjectList;
